import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  isEmptyValue,
  restrictApi,
  serialize,
  useQuery,
} from '@wyz/utils';
import { omitBy } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import {
  IDimensionParameterQuery,
  IDimensionParameterResult,
  ISeason,
} from '~/purchase/types';

type VehicleTypeGroup = {
  codename: string;
  types: Array<number>;
};
type Options = {
  getDimensions?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDimensionParameterResult>>,
      IErrorApiResponse
    >
  > & {
    args: IDimensionParameterQuery;
  };
  getVehicleTypes?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<VehicleTypeGroup>>,
      IErrorApiResponse
    >
  >;
  getSeasons?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<ISeason>>, IErrorApiResponse>
  >;
};

export const useManageParameters = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { state } = usePurchaseContext();
  const { tenantCode } = useConfig();

  const idCustomer = state.targetCustomer?.id as string;

  const getDimensionsQuery = useQuery({
    queryKey: PurchaseQueryKeys.parameters.dimension(
      idCustomer,
      options.getDimensions?.args,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<Array<IDimensionParameterResult>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${idCustomer}/tyres/parameters/dimensions?${serialize(
          omitBy(
            {
              'vehicle-types': options.getDimensions?.args.vehicleTypes,
            },
            isEmptyValue,
          ),
        )}`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    ...options.getDimensions,
    enabled: Boolean(
      options.getDimensions?.args &&
        options.getDimensions?.args.vehicleTypes.length > 0,
    ),
  });

  const getSeasonsQuery = useQuery({
    queryKey: PurchaseQueryKeys.parameters.seasons(idCustomer),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<ISeason>>>(
        `/offers/api/catalog/parameters/seasons`,
      ),
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getSeasons,
  });

  return restrictApi(options, {
    getDimensions: getDimensionsQuery,
    getSeasons: getSeasonsQuery,
  });
};
